import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, EmptyState, ErrorState, Pager, SkeletonRows, TokenLogo, usePaged } from "@/components/common";
import { fmtCompact, fmtUsd } from "@/lib/format";
import { usePageTitle } from "@/lib/seo";

const SORTS = [
  { id: "market_cap", label: "Trending" },
  { id: "volume", label: "Top Volume" },
  { id: "holders", label: "Most Holders" },
];

export default function TokensPage() {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("market_cap");
  usePageTitle("Token Discovery", "Discover trending tokens, top volume and most held tokens live on Robinhood Chain.");
  const pager = usePaged((p) => api.get("/tokens", { params: { ...(query ? { q: query } : {}), ...p } }).then((r) => r.data), [query]);

  const items = useMemo(() => {
    const list = [...pager.items];
    if (sort === "volume") list.sort((a, b) => Number(b.volume_24h || 0) - Number(a.volume_24h || 0));
    if (sort === "holders") list.sort((a, b) => Number(b.holders_count || 0) - Number(a.holders_count || 0));
    return list;
  }, [pager.items, sort]);

  return (
    <div className="space-y-6 pt-8">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Token Discovery</h1>
        <p className="text-sm text-muted-foreground mt-1">Explore tokens live on Robinhood Chain — sorted by real onchain data.</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <form onSubmit={(e) => { e.preventDefault(); setQuery(q); }} className="flex gap-2">
          <input data-testid="tokens-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tokens by name or symbol…"
            className="w-64 bg-card border rounded-lg px-3 py-2 text-xs outline-none focus:border-primary" />
          <button data-testid="tokens-search-btn" className="text-xs rounded-lg px-3 py-2 bg-primary text-primary-foreground font-semibold">Search</button>
        </form>
        <div className="flex gap-2 ml-auto">
          {SORTS.map((s) => (
            <button key={s.id} data-testid={`tokens-sort-${s.id}`} onClick={() => setSort(s.id)}
              className={`text-xs rounded-full px-3 py-1.5 border transition-colors duration-150 ${sort === s.id ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <Card>
        {pager.loading ? <SkeletonRows n={12} /> : pager.error ? <ErrorState /> : !items.length ? (
          <EmptyState title="No tokens found" subtitle="Try a different search term." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                  <th className="px-4 py-2.5 font-medium">Token</th>
                  <th className="px-4 py-2.5 font-medium text-right">Price</th>
                  <th className="px-4 py-2.5 font-medium text-right">Market Value</th>
                  <th className="px-4 py-2.5 font-medium text-right">24h Volume</th>
                  <th className="px-4 py-2.5 font-medium text-right">Holders</th>
                  <th className="px-4 py-2.5 font-medium text-right">Type</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.address_hash} className="table-row-hover border-b last:border-0" data-testid="token-list-row">
                    <td className="px-4 py-3">
                      <Link to={`/token/${t.address_hash}`} className="flex items-center gap-2.5 min-w-0">
                        <TokenLogo url={t.icon_url} symbol={t.symbol} size={26} />
                        <span className="min-w-0">
                          <span className="block text-sm truncate max-w-[220px] hover:text-primary">{t.name || "Unnamed token"}</span>
                          <span className="block text-[10px] text-muted-foreground uppercase">{t.symbol}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{t.exchange_rate ? fmtUsd(t.exchange_rate) : <span className="text-muted-foreground">Price unavailable</span>}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{t.circulating_market_cap ? fmtUsd(t.circulating_market_cap, 0) : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{t.volume_24h ? fmtUsd(t.volume_24h, 0) : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{fmtCompact(t.holders_count)}</td>
                    <td className="px-4 py-3 text-right text-[10px] text-muted-foreground">{t.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!pager.loading && items.length > 0 && <Pager pager={pager} />}
      </Card>
    </div>
  );
}
